/**
 * MIS TRABAJOS — F10.3 (OPERARIO) · F14.2 (ejecución propia, en línea y sin conexión)
 *
 * «¿Qué tengo que hacer ahora?»: el operario entra y ve su jornada en el orden en que la va a hacer — Ahora,
 * Siguiente, Más tarde y Terminadas — y, dentro de cada OT, sus tareas como tarjetas con instrucciones, material
 * previsto, documentos e incidencias.
 *
 * F14.2 — y las EJECUTA: «Hecho», «No conforme» (con motivo), «No aplica» (con motivo), «Rehacer» (si una NC cubre
 * la tarea, explicando la corrección) y «Añadir foto». El ejecutor real es siempre él (lo pone el servidor). Nada de
 * verificar, cerrar la OT, gestionar NC, consumos ni entregas: eso sigue siendo de quien gestiona.
 *
 * UNA pantalla, dos fuentes (mismo modelo funcional):
 *   · con conexión, el servidor (`/api/mis-ots`), con las acciones guardadas en el dispositivo y aún sin llegar
 *     proyectadas encima;
 *   · sin conexión (o con el servidor caído), los datos del dispositivo (`offline/mis-trabajos-local.ts`), tras el
 *     PIN local. Lo que se hace se guarda en el dispositivo y se sincroniza solo al volver la conexión.
 * Las acciones van siempre por `offline/ejecucion-propia.ts`, que decide el camino; la pantalla sólo cuenta dónde
 * quedó cada cosa: «Guardado en este dispositivo · pendiente de sincronizar» o «Guardado en el servidor».
 *
 * Caducidad (`offline/caducidad.ts`): con datos del dispositivo de más de 24 h, aviso; de más de 72 h, sólo se
 * termina lo ya empezado. Con conexión, los datos del dispositivo se actualizan solos.
 *
 * Estados en lenguaje operativo, sin inventar ninguno: es sólo cómo se nombran los existentes.
 */
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';

import { api, descargarConSesion, type MiTarea, type MiTrabajo, type MiTrabajoDetalle, type PaqueteDocumentoOffline, type Usuario } from '../api.ts';
import { Cargando, Error as ErrorCaja, rangoHorario, useDatos, Vacio } from '../componentes/base.tsx';
import { usePerfilOfflineDe } from '../componentes/indicador-sync.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import {
  antiguedadDatos,
  AVISO_DATOS_DESACTUALIZADOS,
  decidirPorCaducidad,
  nivelCaducidad,
  type NivelCaducidad,
} from '../offline/caducidad.ts';
import { useSesionOfflineActiva } from '../offline/contextoPerfil.tsx';
import { leerMeta, type DocumentoOfflineLocal, type MetaOffline } from '../offline/db.ts';
import { ejecutarTareaPropia, subirFotoTarea, type AccionTarea, type ContextoEjecucion } from '../offline/ejecucion-propia.ts';
import { GUARDADO_EN_DISPOSITIVO, GUARDADO_EN_SERVIDOR, type PresentacionEstado } from '../offline/estados-sync.ts';
import { estadoColaPorTarea, leerJornadaLocal, leerTrabajoLocal, proyectarColaSobre } from '../offline/mis-trabajos-local.ts';
import { suscribirCambiosCola } from '../offline/operaciones.ts';
import { prepararPaqueteOffline } from '../offline/paquete.ts';
import { useEstadoBloqueoOffline } from '../offline/perfil.ts';
import { sincronizarPendientes } from '../offline/sync.ts';
import { registrarUrlTemporal, revocarUrlTemporal } from '../offline/urlsTemporales.ts';
import { useConectividad } from '../pwa.ts';
import { BotonAbrirDocumentoLocal } from './offline.tsx';
import { PuertaOfflineSegura } from './offline-perfil.tsx';
import { PaginaMiOt, PaginaMisOts } from './portal.tsx';

const TACTIL = { minHeight: 44 } as const;

/**
 * `/mis-ots` es «lo mío» para dos personas que nunca coinciden: el CLIENTE (portal, `ots:leer_propias`)
 * y, desde F10.3, el OPERARIO (su jornada). Misma URL, una pantalla por persona; ninguna se mezcla.
 */
export function esOperarioDeTrabajo(roles: readonly { rol: string }[], puede: (p: string) => boolean): boolean {
  return roles.some((r) => r.rol === 'OPERARIO') && !puede('ots:leer_propias') && !puede('ots:leer');
}

export function RutaMisOts(): ReactNode {
  const { usuario, puede } = useSesion();
  return usuario !== null && esOperarioDeTrabajo(usuario.roles, puede) ? <PaginaMisTrabajos /> : <PaginaMisOts />;
}

export function RutaMiOt(): ReactNode {
  const { usuario, puede } = useSesion();
  return usuario !== null && esOperarioDeTrabajo(usuario.roles, puede) ? <PaginaMiTrabajo /> : <PaginaMiOt />;
}

/**
 * F14.2 — `/mis-ots*` abierto SIN sesión de servidor (recarga o arranque sin conexión): sólo el PIN local, y sólo
 * para un perfil de OPERARIO. Cualquier otro perfil local sigue en `/offline`. El PIN no autentica contra el
 * servidor: sólo descifra lo que ya está en el dispositivo.
 */
export function MisTrabajosSinSesion({ detalle }: { detalle: boolean }): ReactNode {
  return (
    <PuertaOfflineSegura>
      <SoloOperarioLocal>{detalle ? <MiTrabajoEnDispositivo /> : <MisTrabajosEnDispositivo />}</SoloOperarioLocal>
    </PuertaOfflineSegura>
  );
}

function SoloOperarioLocal({ children }: { children: ReactNode }): ReactNode {
  const { roles } = useSesionOfflineActiva();
  return roles.some((r) => r.rol === 'OPERARIO') ? <>{children}</> : <Navigate to="/offline" replace />;
}

/** Estado de UNA tarea, como lo diría quien la hace. */
export function estadoTareaOperario(estado: string): { texto: string; tono: string } {
  switch (estado) {
    case 'PENDIENTE': return { texto: 'Pendiente', tono: 'neutra' };
    case 'CONFIRMADA':
    case 'VERIFICADA': return { texto: 'Realizada', tono: 'exito' };
    case 'NO_OK': return { texto: 'No conforme', tono: 'error' };
    case 'NO_APLICA': return { texto: 'No aplica', tono: 'neutra' };
    default: return { texto: estado.toLowerCase(), tono: 'neutra' };
  }
}

/** Estado de una OT para el operario: si hay que ir, si está en marcha o si ya está hecha. */
export function estadoTrabajoOperario(estado: string): { texto: string; tono: string } {
  switch (estado) {
    case 'PLANIFICADA': return { texto: 'Programada', tono: 'info' };
    case 'EN_CURSO': return { texto: 'En curso', tono: 'aviso' };
    case 'COMPLETADA':
    case 'VERIFICADA':
    case 'CERRADA':
    case 'REABIERTA': return { texto: 'Terminada', tono: 'exito' };
    case 'NO_REALIZADA': return { texto: 'No realizada', tono: 'neutra' };
    case 'ANULADA': return { texto: 'Anulada', tono: 'neutra' };
    default: return { texto: estado.toLowerCase(), tono: 'neutra' };
  }
}

/**
 * «07:00–08:30» en la hora local del dispositivo, a partir del instante UTC real (F10.0); «(+1 día)»
 * si termina al día siguiente (F10.4).
 */
export function horarioTrabajo(t: Pick<MiTrabajo, 'inicioProgramadoEn' | 'duracionMin'>): string {
  if (t.inicioProgramadoEn === null) return 'Sin hora';
  const inicio = new Date(t.inicioProgramadoEn);
  return rangoHorario(inicio.getHours() * 60 + inicio.getMinutes(), t.duracionMin);
}

function terminado(t: MiTrabajo): boolean {
  return ['COMPLETADA', 'VERIFICADA', 'CERRADA', 'REABIERTA'].includes(t.estado)
    || (t.misTareas.total > 0 && t.misTareas.hechas === t.misTareas.total);
}

/** Ahora = en curso y sin terminar; Siguiente = la primera programada; Más tarde = el resto. */
export function agruparJornada(trabajos: readonly MiTrabajo[]): {
  ahora: MiTrabajo[]; siguiente: MiTrabajo[]; masTarde: MiTrabajo[]; terminadas: MiTrabajo[];
} {
  const terminadas = trabajos.filter(terminado);
  const abiertos = trabajos.filter((t) => !terminado(t) && t.estado !== 'ANULADA' && t.estado !== 'NO_REALIZADA');
  const ahora = abiertos.filter((t) => t.estado === 'EN_CURSO');
  const programadas = abiertos.filter((t) => t.estado !== 'EN_CURSO');
  return { ahora, siguiente: programadas.slice(0, 1), masTarde: programadas.slice(1), terminadas };
}

/**
 * F14.2 — qué puede hacer el operario con SU tarea desde su estado (hint de UX; el servidor decide). Mismas
 * transiciones que `fn_ot_linea_evento_registrar`: Hecho / No conforme / No aplica desde Pendiente; No conforme
 * desde Realizada (sin verificar); Rehacer desde No conforme sólo si una NC la cubre.
 */
export function accionesDeTarea(t: Pick<MiTarea, 'estado' | 'tieneNc'>): readonly AccionTarea[] {
  switch (t.estado) {
    case 'PENDIENTE': return ['HECHO', 'NO_CONFORME', 'NO_APLICA'];
    case 'CONFIRMADA': return ['NO_CONFORME'];
    case 'NO_OK': return t.tieneNc ? ['REHACER'] : [];
    default: return [];
  }
}

const TEXTO_BOTON: Readonly<Record<AccionTarea, string>> = {
  HECHO: 'Hecho',
  NO_CONFORME: 'No conforme',
  NO_APLICA: 'No aplica',
  REHACER: 'Rehacer',
};

function Atencion({ noConformes, incidencias }: { noConformes: number; incidencias: number }): ReactNode {
  if (noConformes === 0 && incidencias === 0) return null;
  return (
    <span className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
      {noConformes > 0 && <span className="etiqueta error">{noConformes === 1 ? '1 tarea no conforme' : `${String(noConformes)} tareas no conformes`}</span>}
      {incidencias > 0 && <span className="etiqueta aviso">Con incidencia</span>}
    </span>
  );
}

// ------------------------------------------------------------------ perfil del dispositivo (en línea)

/** El perfil offline de ESTE usuario, sólo si está desbloqueado (para leer y escribir su cola cifrada). */
export function usePerfilDesbloqueadoDe(usuario: Usuario | null): string | null {
  const perfilId = usePerfilOfflineDe(usuario);
  const bloqueo = useEstadoBloqueoOffline();
  return bloqueo?.estado === 'UNLOCKED' && bloqueo.perfilId === perfilId ? perfilId : null;
}

/**
 * Se relee cuando cambia la cola (sincronización automática, otra acción). Una sola suscripción por montaje y la
 * última `recargar` en una ref: si quien llama pasa una función nueva en cada render (p. ej. la de `useDatos`),
 * volver a suscribirse cancelaba la relectura pendiente y se perdía el último cambio (F14.3, UAT: el consumo en
 * CONFLICTO_STOCK seguía como «pendiente de sincronizar»).
 */
export function useCambiosCola(recargar: () => void): void {
  const ultima = useRef(recargar);
  useEffect(() => { ultima.current = recargar; });
  useEffect(() => {
    let pendiente: ReturnType<typeof setTimeout> | null = null;
    const cancelar = suscribirCambiosCola(() => {
      if (pendiente !== null) return;
      pendiente = setTimeout(() => {
        pendiente = null;
        ultima.current();
      }, 150);
    });
    return () => {
      cancelar();
      if (pendiente !== null) clearTimeout(pendiente);
    };
  }, []);
}

export function useMeta(perfilId: string | null): { meta: MetaOffline | null; releer: () => void } {
  const [meta, setMeta] = useState<MetaOffline | null>(null);
  const [ronda, setRonda] = useState(0);
  useEffect(() => {
    let vivo = true;
    if (perfilId === null) {
      setMeta(null);
      return;
    }
    void leerMeta(perfilId).then((m) => { if (vivo) setMeta(m); }).catch(() => { if (vivo) setMeta(null); });
    return () => { vivo = false; };
  }, [perfilId, ronda]);
  return { meta, releer: useCallback(() => setRonda((n) => n + 1), []) };
}

/**
 * Con conexión y el dispositivo preparado: los datos del dispositivo se ponen al día solos cuando ya no son de hoy
 * (más de 24 h) — primero se envía lo pendiente, después se descarga. Así, al quedarse sin conexión, lo que hay en
 * el dispositivo es lo último que dijo el servidor.
 */
export function useActualizarDatosDispositivo(perfilId: string | null, meta: MetaOffline | null, alTerminar: () => void): { actualizando: boolean; actualizar: () => void; error: string | null } {
  const [actualizando, setActualizando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const actualizar = useCallback(() => {
    if (perfilId === null) return;
    setActualizando(true);
    setError(null);
    void (async () => {
      try {
        await sincronizarPendientes(perfilId);
        await prepararPaqueteOffline(perfilId);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se han podido actualizar los datos.');
      } finally {
        setActualizando(false);
        alTerminar();
      }
    })();
  }, [perfilId, alTerminar]);
  const nivel = meta === null ? null : nivelCaducidad(meta);
  useEffect(() => {
    if (nivel === 'AVISO' || nivel === 'CADUCADO') actualizar();
    // Una vez por perfil y tramo: no se repite en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilId, nivel]);
  return { actualizando, actualizar, error };
}

function PanelDispositivo({ perfilId, meta, actualizando, actualizar, error }: {
  perfilId: string | null; meta: MetaOffline | null; actualizando: boolean; actualizar: () => void; error: string | null;
}): ReactNode {
  return (
    <div className="panel" aria-label="Trabajo sin conexión">
      <div className="panel-cuerpo fila" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Link to="/reportar" className="boton" style={{ ...TACTIL, display: 'inline-flex', alignItems: 'center' }}>Reportar un problema</Link>
        {perfilId === null || meta === null ? (
          //  Sin conexión es una preparación aparte: nunca hace falta para ver ni hacer el trabajo con cobertura.
          <Link to="/offline" className="boton" style={{ ...TACTIL, display: 'inline-flex', alignItems: 'center' }}>Preparar sin conexión</Link>
        ) : (
          <>
            <span className="suave pequena">Datos en este dispositivo: {antiguedadDatos(meta.generadoEn)}</span>
            <button type="button" style={TACTIL} disabled={actualizando} onClick={actualizar}>{actualizando ? 'Actualizando…' : 'Actualizar datos'}</button>
          </>
        )}
        {error !== null && <span className="nota error pequena" role="alert">{error}</span>}
      </div>
    </div>
  );
}

/** Aviso general del tramo; lo que se puede hacer en cada tarea lo dice su tarjeta (la unidad es la tarea). */
export function AvisoCaducidad({ nivel }: { nivel: NivelCaducidad }): ReactNode {
  if (nivel === 'VIGENTE') return null;
  return (
    <p className={`nota ${nivel === 'CADUCADO' ? 'error' : 'aviso'}`} role="status">
      {nivel === 'CADUCADO'
        ? 'Los datos de este dispositivo tienen más de 72 h: puedes terminar las tareas que ya empezaste, pero no empezar tareas nuevas hasta conectarte y actualizar.'
        : AVISO_DATOS_DESACTUALIZADOS}
    </p>
  );
}

// ------------------------------------------------------------------ jornada

export function PaginaMisTrabajos(): ReactNode {
  const conectividad = useConectividad();
  const { usuario } = useSesion();
  if (conectividad === 'ONLINE' && usuario !== null) return <MisTrabajosEnLinea usuario={usuario} />;
  return <PuertaOfflineSegura><MisTrabajosEnDispositivo /></PuertaOfflineSegura>;
}

function MisTrabajosEnLinea({ usuario }: { usuario: Usuario }): ReactNode {
  const jornada = useDatos(() => api.get<{ filas: MiTrabajo[] }>('/api/mis-ots?vista=jornada'), []);
  const perfilId = usePerfilDesbloqueadoDe(usuario);
  const { meta, releer } = useMeta(perfilId);
  const dispositivo = useActualizarDatosDispositivo(perfilId, meta, releer);
  return (
    <Shell titulo="Mis trabajos">
      {jornada.cargando && jornada.datos === null && <Cargando texto="Cargando tu jornada…" />}
      {jornada.error !== null && <ErrorCaja error={jornada.error} reintentar={jornada.recargar} />}
      {jornada.datos !== null && <Jornada trabajos={jornada.datos.filas} />}
      <PanelDispositivo perfilId={perfilId} meta={meta} {...dispositivo} />
    </Shell>
  );
}

function MisTrabajosEnDispositivo(): ReactNode {
  const { perfilId } = useSesionOfflineActiva();
  const [trabajos, setTrabajos] = useState<MiTrabajo[] | null>(null);
  const [error, setError] = useState(false);
  const { meta, releer } = useMeta(perfilId);
  const cargar = useCallback(() => {
    void leerJornadaLocal(perfilId).then((t) => { setTrabajos(t); setError(false); }).catch(() => setError(true));
    releer();
  }, [perfilId, releer]);
  useEffect(cargar, [cargar]);
  useCambiosCola(cargar);
  return (
    <Shell titulo="Mis trabajos">
      <p className="nota info" role="status">Sin conexión con el servidor: trabajas con los datos guardados en este dispositivo{meta !== null ? ` (${antiguedadDatos(meta.generadoEn)})` : ''}. Lo que hagas se enviará solo al volver la conexión.</p>
      {meta !== null && <AvisoCaducidad nivel={nivelCaducidad(meta)} />}
      {error && <Vacio titulo="No se han podido leer los datos de este dispositivo. Vuelve a prepararlos cuando tengas conexión." />}
      {!error && trabajos === null && <Cargando texto="Cargando tu jornada…" />}
      {trabajos !== null && <Jornada trabajos={trabajos} />}
    </Shell>
  );
}

function Jornada({ trabajos }: { trabajos: readonly MiTrabajo[] }): ReactNode {
  if (trabajos.length === 0) return <Vacio titulo="Hoy no tienes trabajos asignados." />;
  const grupos = agruparJornada(trabajos);
  return (
    <>
      <GrupoTrabajos titulo="Ahora" trabajos={grupos.ahora} vacio="Nada en curso ahora mismo." destacado />
      <GrupoTrabajos titulo="Siguiente" trabajos={grupos.siguiente} vacio="No te queda nada más programado hoy." />
      {grupos.masTarde.length > 0 && <GrupoTrabajos titulo="Más tarde" trabajos={grupos.masTarde} />}
      {grupos.terminadas.length > 0 && <GrupoTrabajos titulo="Terminadas" trabajos={grupos.terminadas} />}
    </>
  );
}

function GrupoTrabajos({ titulo, trabajos, vacio, destacado = false }: {
  titulo: string; trabajos: readonly MiTrabajo[]; vacio?: string; destacado?: boolean;
}): ReactNode {
  if (trabajos.length === 0 && vacio === undefined) return null;
  return (
    <section className="panel" aria-label={titulo}>
      <div className="panel-cabecera"><h2>{titulo}</h2></div>
      <div className="panel-cuerpo">
        {trabajos.length === 0 ? (
          <p className="suave" style={{ margin: 0 }}>{vacio}</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {trabajos.map((t) => <TarjetaTrabajo key={t.id} t={t} destacado={destacado} />)}
          </ul>
        )}
      </div>
    </section>
  );
}

function TarjetaTrabajo({ t, destacado }: { t: MiTrabajo; destacado: boolean }): ReactNode {
  const estado = estadoTrabajoOperario(t.estado);
  return (
    <li className="tarjeta-ot" style={destacado ? { borderLeft: '4px solid var(--marca)' } : undefined}>
      <Link to={`/mis-ots/${t.id}`} aria-label={`Abrir trabajo ${t.salaDescripcion} ${horarioTrabajo(t)}`}
        style={{ display: 'block', color: 'inherit', textDecoration: 'none', minHeight: 44 }}>
        <div className="tarjeta-ot-cabecera">
          <strong style={{ fontSize: 17 }}>{horarioTrabajo(t)}</strong>
          <span className={`etiqueta ${estado.tono}`}>{estado.texto}</span>
        </div>
        <div className="tarjeta-ot-datos">
          <div className="tarjeta-ot-fecha">{t.salaDescripcion}</div>
          <div className="suave pequena">{t.centroDescripcion}</div>
          <div>{t.misTareas.total === 0 ? 'Sin tareas' : `${String(t.misTareas.hechas)} de ${String(t.misTareas.total)} tareas hechas`}</div>
        </div>
        <div className="tarjeta-ot-secundario">
          <Atencion noConformes={t.misTareas.noConformes} incidencias={t.incidenciasAbiertas} />
          <span className="tenue pequena">{t.codigo}</span>
        </div>
      </Link>
    </li>
  );
}

// ------------------------------------------------------------------ un trabajo

export function PaginaMiTrabajo(): ReactNode {
  const conectividad = useConectividad();
  const { usuario } = useSesion();
  if (conectividad === 'ONLINE' && usuario !== null) return <MiTrabajoEnLinea usuario={usuario} />;
  return <PuertaOfflineSegura><MiTrabajoEnDispositivo /></PuertaOfflineSegura>;
}

/** Lo que una tarjeta de tarea necesita para actuar y para contar dónde quedó cada cosa. */
interface Entorno {
  readonly ctx: ContextoEjecucion;
  /** Por TAREA (`caducidad.ts`): `null` = se puede actuar en ella; un texto = por qué no (caducidad). */
  readonly bloqueoDe: (idLinea: string) => string | null;
  readonly colas: ReadonlyMap<string, PresentacionEstado>;
  readonly alCambiar: () => void;
  readonly abrirDocumento: (doc: PaqueteDocumentoOffline) => ReactNode;
  readonly enLinea: boolean;
}

function MiTrabajoEnLinea({ usuario }: { usuario: Usuario }): ReactNode {
  const { idOt } = useParams<{ idOt: string }>();
  const perfilId = usePerfilDesbloqueadoDe(usuario);
  const ot = useDatos(async () => {
    const d = await api.get<MiTrabajoDetalle>(`/api/mis-ots/${idOt ?? ''}`);
    return perfilId === null ? d : proyectarColaSobre(perfilId, d).catch(() => d);
  }, [idOt, perfilId]);
  const [colas, setColas] = useState<ReadonlyMap<string, PresentacionEstado>>(new Map());
  const releerColas = useCallback(() => {
    if (perfilId === null || idOt === undefined) return;
    void estadoColaPorTarea(perfilId, idOt).then(setColas).catch(() => undefined);
  }, [perfilId, idOt]);
  useEffect(releerColas, [releerColas]);
  const { recargar } = ot;
  const alCambiar = useCallback(() => { recargar(); releerColas(); }, [recargar, releerColas]);
  useCambiosCola(alCambiar);
  const migas = [{ texto: 'Mis trabajos', a: '/mis-ots' }, { texto: ot.datos?.salaDescripcion ?? '…' }];
  if (ot.cargando && ot.datos === null) return <Shell titulo="Trabajo" migas={migas}><Cargando /></Shell>;
  if (ot.error !== null || ot.datos === null) return <Shell titulo="Trabajo" migas={migas}><ErrorCaja error={ot.error} reintentar={ot.recargar} /></Shell>;
  const entorno: Entorno = {
    ctx: { perfilId, roles: usuario.roles, enLinea: true },
    bloqueoDe: () => null,
    colas,
    alCambiar,
    abrirDocumento: (doc) => <BotonAbrirDocumentoEnLinea idOt={ot.datos!.id} doc={doc} />,
    enLinea: true,
  };
  return <VistaTrabajo d={ot.datos} entorno={entorno} migas={migas} aviso={null} />;
}

function MiTrabajoEnDispositivo(): ReactNode {
  const { idOt } = useParams<{ idOt: string }>();
  const { perfilId, roles } = useSesionOfflineActiva();
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof leerTrabajoLocal>> | undefined>(undefined);
  const [colas, setColas] = useState<ReadonlyMap<string, PresentacionEstado>>(new Map());
  const { meta, releer } = useMeta(perfilId);
  const cargar = useCallback(() => {
    if (idOt === undefined) return;
    void leerTrabajoLocal(perfilId, idOt).then(setDatos).catch(() => setDatos(null));
    void estadoColaPorTarea(perfilId, idOt).then(setColas).catch(() => undefined);
    releer();
  }, [perfilId, idOt, releer]);
  useEffect(cargar, [cargar]);
  useCambiosCola(cargar);
  const migas = [{ texto: 'Mis trabajos', a: '/mis-ots' }, { texto: datos?.detalle.salaDescripcion ?? '…' }];
  if (datos === undefined) return <Shell titulo="Trabajo" migas={migas}><Cargando /></Shell>;
  if (datos === null) {
    return (
      <Shell titulo="Trabajo" migas={migas}>
        <Vacio titulo="Este trabajo no está en los datos de este dispositivo." />
      </Shell>
    );
  }
  const nivel = meta === null ? 'VIGENTE' : nivelCaducidad(meta);
  const documentos = new Map(datos.documentos.map((d) => [d.id, d] as const));
  const entorno: Entorno = {
    ctx: { perfilId, roles, enLinea: false },
    bloqueoDe: (idLinea) => {
      const decision = decidirPorCaducidad(nivel, datos.tareasIniciadas.has(idLinea));
      return decision.permitido ? null : decision.motivo;
    },
    colas,
    alCambiar: cargar,
    abrirDocumento: (doc) => {
      const local: DocumentoOfflineLocal | undefined = documentos.get(doc.id);
      return local === undefined ? null : <BotonAbrirDocumentoLocal idPerfil={perfilId} doc={local} />;
    },
    enLinea: false,
  };
  return <VistaTrabajo d={datos.detalle} entorno={entorno} migas={migas} aviso={<AvisoCaducidad nivel={nivel} />} />;
}

function VistaTrabajo({ d, entorno, migas, aviso }: {
  d: MiTrabajoDetalle; entorno: Entorno; migas: { texto: string; a?: string }[]; aviso: ReactNode;
}): ReactNode {
  const ubicacion = useLocation();
  const estado = estadoTrabajoOperario(d.estado);
  const pendientes = d.lineas.filter((l) => l.estado === 'PENDIENTE');
  //  Cada tarea en UN solo grupo: lo pendiente sigue en «Qué hacer ahora» (con su aviso de incidencia si la
  //  tiene); «Con problema» es lo ya hecho que no quedó bien (No conforme) o que tiene una incidencia abierta.
  const conProblema = d.lineas.filter((l) => l.estado !== 'PENDIENTE' && (l.estado === 'NO_OK' || l.incidenciasAbiertas > 0));
  const resueltas = d.lineas.filter((l) => l.estado !== 'PENDIENTE' && !conProblema.includes(l));
  const volver = { a: `${ubicacion.pathname}${ubicacion.search}`, texto: 'Volver al trabajo' };
  const documentos = d.documentos ?? [];
  const docsDe = (idLinea: string | null): PaqueteDocumentoOffline[] => documentos.filter((x) => (idLinea === null ? x.idOtLinea === null : x.idOtLinea === idLinea));
  return (
    <Shell titulo={d.salaDescripcion} migas={migas}>
      {!entorno.enLinea && <p className="nota info" role="status">Sin conexión con el servidor: lo que hagas se guarda en este dispositivo y se enviará solo al volver la conexión.</p>}
      {aviso}
      <div className="panel" aria-label="Resumen del trabajo">
        <div className="panel-cuerpo">
          <div className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 18 }}>{horarioTrabajo(d)}</strong>
            <span className={`etiqueta ${estado.tono}`}>{estado.texto}</span>
          </div>
          <div className="suave">{d.centroDescripcion}</div>
          <div>{d.misTareas.total === 0 ? 'Sin tareas' : `${String(d.misTareas.hechas)} de ${String(d.misTareas.total)} tareas hechas`}</div>
          <Atencion noConformes={d.misTareas.noConformes} incidencias={d.incidenciasAbiertas} />
          {d.observaciones !== null && d.observaciones.trim() !== '' && (
            <p className="nota" style={{ marginBottom: 0 }}><strong>Indicaciones:</strong> {d.observaciones}</p>
          )}
          <ListaDocumentos documentos={docsDe(null)} entorno={entorno} />
        </div>
      </div>

      {pendientes.length > 0 && (
        <section className="panel" aria-label="Qué hacer ahora">
          <div className="panel-cabecera"><h2>Qué hacer ahora</h2></div>
          <div className="panel-cuerpo">
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pendientes.map((l, i) => (
                <TarjetaTarea key={l.id} idOt={d.id} tarea={l} volver={volver} entorno={entorno} documentos={docsDe(l.id)}
                  rotulo={i === 0 ? 'Tarea actual' : i === 1 ? 'Siguiente' : null} />
              ))}
            </ul>
          </div>
        </section>
      )}
      {conProblema.length > 0 && <GrupoTareas titulo="Con problema" idOt={d.id} tareas={conProblema} volver={volver} entorno={entorno} docsDe={docsDe} />}
      {resueltas.length > 0 && <GrupoTareas titulo="Hechas" idOt={d.id} tareas={resueltas} volver={volver} entorno={entorno} docsDe={docsDe} />}
      {d.lineas.length === 0 && <Vacio titulo="Este trabajo no tiene tareas tuyas." />}
    </Shell>
  );
}

function ListaDocumentos({ documentos, entorno }: { documentos: readonly PaqueteDocumentoOffline[]; entorno: Entorno }): ReactNode {
  if (documentos.length === 0) return null;
  return (
    <div className="pequena" style={{ marginTop: 6 }}>
      <span className="suave">Documentos:</span>
      <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
        {documentos.map((doc) => (
          <li key={doc.id} className="fila" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{doc.titulo} <span className="suave">v{doc.version}</span></span>
            {entorno.abrirDocumento(doc)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** En línea, por la ruta propia del operario (los mismos documentos que lleva su paquete). */
function BotonAbrirDocumentoEnLinea({ idOt, doc }: { idOt: string; doc: PaqueteDocumentoOffline }): ReactNode {
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);
  async function abrir(): Promise<void> {
    setCargando(true);
    setFallo(false);
    const blob = await descargarConSesion(`/api/mis-ots/${idOt}/documentos/${doc.id}/archivo`).catch(() => null);
    setCargando(false);
    if (blob === null) { setFallo(true); return; }
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    registrarUrlTemporal(url);
    setTimeout(() => revocarUrlTemporal(url), 60_000);
  }
  return (
    <span className="fila" style={{ gap: 6, alignItems: 'center' }}>
      <button type="button" className="pequeno" style={TACTIL} disabled={cargando} onClick={() => void abrir()}>{cargando ? 'Abriendo…' : 'Abrir'}</button>
      {fallo && <span className="suave pequena">No se ha podido abrir.</span>}
    </span>
  );
}

function GrupoTareas({ titulo, idOt, tareas, volver, entorno, docsDe }: {
  titulo: string; idOt: string; tareas: readonly MiTarea[]; volver: { a: string; texto: string }; entorno: Entorno;
  docsDe: (idLinea: string | null) => PaqueteDocumentoOffline[];
}): ReactNode {
  return (
    <section className="panel" aria-label={titulo}>
      <div className="panel-cabecera"><h2>{titulo}</h2></div>
      <div className="panel-cuerpo">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tareas.map((l) => <TarjetaTarea key={l.id} idOt={idOt} tarea={l} volver={volver} rotulo={null} entorno={entorno} documentos={docsDe(l.id)} />)}
        </ul>
      </div>
    </section>
  );
}

type Resultado = { readonly tono: 'exito' | 'info' | 'error'; readonly texto: string };

/** Una tarea: qué, dónde, cómo, con qué material, qué hacer — y dónde quedó lo último que se hizo. */
function TarjetaTarea({ idOt, tarea, volver, rotulo, entorno, documentos }: {
  idOt: string; tarea: MiTarea; volver: { a: string; texto: string }; rotulo: string | null; entorno: Entorno;
  documentos: readonly PaqueteDocumentoOffline[];
}): ReactNode {
  const estado = estadoTareaOperario(tarea.estado);
  const [foto, setFoto] = useState(false);
  const [formulario, setFormulario] = useState<AccionTarea | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const cola = entorno.colas.get(tarea.id);
  const lugar = tarea.ubicacionDescripcion !== '' ? tarea.ubicacionDescripcion : (tarea.descripcionTarea ?? tarea.tipoDescripcion);
  const bloqueo = entorno.bloqueoDe(tarea.id);
  const acciones = bloqueo === null ? accionesDeTarea(tarea) : [];

  async function hacer(accion: AccionTarea, texto: string | null): Promise<void> {
    setEnviando(true);
    setResultado(null);
    const r = await ejecutarTareaPropia(entorno.ctx, {
      idOt, idLinea: tarea.id, accion, lugar,
      motivo: accion === 'NO_CONFORME' || accion === 'NO_APLICA' ? texto : null,
      observacion: accion === 'REHACER' ? texto : null,
    });
    setEnviando(false);
    if (!r.ok) { setResultado({ tono: 'error', texto: r.motivo }); return; }
    setFormulario(null);
    setResultado(r.donde === 'SERVIDOR'
      ? { tono: 'exito', texto: GUARDADO_EN_SERVIDOR }
      : { tono: 'info', texto: r.aviso === null ? GUARDADO_EN_DISPOSITIVO : `${GUARDADO_EN_DISPOSITIVO}. ${r.aviso}` });
    entorno.alCambiar();
  }

  return (
    <li className="tarjeta-linea" aria-label={`Tarea ${tarea.descripcionTarea ?? tarea.tipoDescripcion}`}>
      <div className="tarjeta-linea-cabecera">
        <div>
          {rotulo !== null && <div className="etiqueta info pequena" style={{ marginBottom: 4 }}>{rotulo}</div>}
          <div><strong>{tarea.descripcionTarea ?? tarea.tipoDescripcion}</strong></div>
          <div className="pequena suave">{tarea.ubicacionDescripcion} · {tarea.ubicacionRuta}</div>
        </div>
        <span className="fila" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span className={`etiqueta ${estado.tono}`}>{estado.texto}</span>
          {cola !== undefined && <span className={`etiqueta ${cola.clase}`}>{cola.texto}</span>}
        </span>
      </div>
      <div className="tarjeta-linea-datos">
        {tarea.descripcionTarea !== null && <div className="pequena"><span className="suave">Cómo:</span> {tarea.tipoDescripcion}</div>}
        {tarea.procedimiento !== null && (
          <div className="pequena">
            <span className="suave">Procedimiento:</span>{' '}
            {tarea.procedimiento.url !== null
              ? <a href={tarea.procedimiento.url} target="_blank" rel="noreferrer">{tarea.procedimiento.nombre}</a>
              : tarea.procedimiento.nombre}
          </div>
        )}
        {tarea.materialesPrevistos.length > 0 && (
          <div className="pequena">
            <span className="suave">Material previsto:</span>
            <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
              {tarea.materialesPrevistos.map((m) => (
                <li key={m.id}>
                  {m.articulo.descripcion} · {m.cantidad} {m.unidad}
                  {m.instrucciones !== null && m.instrucciones !== '' && <span className="suave"> — {m.instrucciones}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <ListaDocumentos documentos={documentos} entorno={entorno} />
        {tarea.estado === 'NO_OK' && tarea.ncAsociada !== null && (
          <div className="pequena"><span className="suave">No conformidad:</span> {tarea.ncAsociada.codigo}</div>
        )}
        {tarea.estado === 'NO_OK' && !tarea.tieneNc && (
          <div className="pequena suave">Pendiente de que tu encargado la revise.</div>
        )}
        {tarea.incidenciasAbiertas > 0 && (
          <div><span className="etiqueta aviso">{tarea.incidenciasAbiertas === 1 ? 'Incidencia abierta' : `${String(tarea.incidenciasAbiertas)} incidencias abiertas`}</span></div>
        )}
      </div>
      {bloqueo !== null && <p className="nota error pequena" role="status" style={{ margin: '6px 0' }}>{bloqueo}</p>}
      {resultado !== null && <p className={`nota ${resultado.tono}`} role={resultado.tono === 'error' ? 'alert' : 'status'} style={{ margin: '6px 0' }}>{resultado.texto}</p>}
      <div className="tarjeta-linea-acciones">
        {acciones.map((a) => (
          <button key={a} type="button" className={a === 'HECHO' ? 'principal' : undefined} style={TACTIL} disabled={enviando}
            aria-expanded={a === 'HECHO' ? undefined : formulario === a}
            onClick={() => (a === 'HECHO' ? void hacer('HECHO', null) : setFormulario((f) => (f === a ? null : a)))}>
            {enviando && a === 'HECHO' ? 'Guardando…' : TEXTO_BOTON[a]}
          </button>
        ))}
        {entorno.enLinea && (
          <Link to={`/reportar?ot=${idOt}&linea=${tarea.id}`} state={{ volver }} className="boton"
            style={{ ...TACTIL, display: 'inline-flex', alignItems: 'center' }}>
            Reportar problema
          </Link>
        )}
        {bloqueo === null && (
          <button type="button" style={TACTIL} aria-expanded={foto} onClick={() => setFoto((v) => !v)}>
            {foto ? 'Cancelar foto' : 'Añadir foto'}
          </button>
        )}
      </div>
      {formulario !== null && formulario !== 'HECHO' && (
        <FormularioAccion accion={formulario} enviando={enviando} onCancelar={() => setFormulario(null)} onEnviar={(t) => void hacer(formulario, t)} />
      )}
      {foto && <FotoTarea idOt={idOt} idLinea={tarea.id} lugar={lugar} ctx={entorno.ctx} alTerminar={() => { setFoto(false); entorno.alCambiar(); }} />}
    </li>
  );
}

/** Motivo (No conforme / No aplica) o corrección (Rehacer): obligatorio, como en el servidor. */
function FormularioAccion({ accion, enviando, onCancelar, onEnviar }: {
  accion: Exclude<AccionTarea, 'HECHO'>; enviando: boolean; onCancelar: () => void; onEnviar: (texto: string) => void;
}): ReactNode {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [falta, setFalta] = useState(false);
  const etiqueta = accion === 'REHACER' ? '¿Qué has corregido?' : accion === 'NO_CONFORME' ? '¿Qué no ha quedado bien?' : '¿Por qué no aplica?';
  return (
    <div className="tarjeta-linea-formulario panel">
      <div className="panel-cuerpo">
        <div className="campo">
          <label htmlFor={`${id}-texto`}>{etiqueta}</label>
          <textarea id={`${id}-texto`} value={texto} onChange={(e) => { setTexto(e.target.value); setFalta(false); }} aria-invalid={falta} />
        </div>
        {falta && <p className="nota error" role="alert">{accion === 'REHACER' ? 'Explica qué has corregido.' : 'Indica el motivo.'}</p>}
        <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="principal" style={TACTIL} disabled={enviando}
            onClick={() => (texto.trim() === '' ? setFalta(true) : onEnviar(texto.trim()))}>
            {enviando ? 'Guardando…' : accion === 'REHACER' ? 'Guardar corrección' : `Guardar «${TEXTO_BOTON[accion]}»`}
          </button>
          <button type="button" style={TACTIL} onClick={onCancelar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/** Foto de la tarea por el endpoint propio del operario (sólo sus líneas; lo comprueba el servidor). Sin conexión, al dispositivo. */
function FotoTarea({ idOt, idLinea, lugar, ctx, alTerminar }: {
  idOt: string; idLinea: string; lugar: string; ctx: ContextoEjecucion; alTerminar: () => void;
}): ReactNode {
  const idBase = useId();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [momento, setMomento] = useState<'ANTES' | 'DESPUES' | 'GENERAL'>('DESPUES');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  async function enviar(): Promise<void> {
    if (archivo === null) { setError('Elige una foto.'); return; }
    setEnviando(true);
    setError(null);
    const r = await subirFotoTarea(ctx, { idOt, idLinea, archivo, contexto: momento, lugar });
    setEnviando(false);
    if (!r.ok) { setError(r.motivo); return; }
    setHecho(r.donde === 'SERVIDOR' ? GUARDADO_EN_SERVIDOR : GUARDADO_EN_DISPOSITIVO);
    setTimeout(alTerminar, 1500);
  }
  return (
    <div className="tarjeta-linea-formulario panel">
      <div className="panel-cuerpo">
        {hecho !== null && (
          <div role="status">
            <p className="nota exito" style={{ marginBottom: 4 }}>Foto añadida.</p>
            <p className="suave pequena" style={{ margin: 0 }}>{hecho}</p>
          </div>
        )}
        {error !== null && <p className="nota error" role="alert">{error}</p>}
        <div className="campo">
          <label htmlFor={`${idBase}-foto`}>Foto</label>
          <input id={`${idBase}-foto`} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} style={TACTIL} />
        </div>
        <div className="campo">
          <label htmlFor={`${idBase}-momento`}>¿Cuándo?</label>
          <select id={`${idBase}-momento`} value={momento} onChange={(e) => setMomento(e.target.value as typeof momento)} style={TACTIL}>
            <option value="ANTES">Antes de limpiar</option>
            <option value="DESPUES">Después de limpiar</option>
            <option value="GENERAL">Otra</option>
          </select>
        </div>
        <button type="button" className="principal" style={TACTIL} disabled={enviando} onClick={() => void enviar()}>
          {enviando ? 'Enviando…' : 'Enviar foto'}
        </button>
      </div>
    </div>
  );
}
